/**
 * RFC 9457 problem details — the single error shape of the API.
 * Every failure (problem+json, HTML 502 from a proxy, network error) is
 * normalised into one `ProblemError` at the fetch boundary. UI code branches on
 * `errorCode` only, never on text.
 */
import i18n from "@/lib/i18n"

export type FieldError = { field: string; error_code: number; message: string }

export type Problem = {
  type: string
  title: string
  status: number
  detail?: string
  error_code: number
  trace_id?: string
  instance?: string
  errors?: FieldError[]
  retry_after_minutes?: number
  existing_customer_id?: string
  attempts_left?: number
  retry_after?: number
  [ext: string]: unknown
}

/** Stable numeric codes (backend app/platform/apperr.py). */
export const ErrorCode = {
  INTERNAL: 1,
  STREAMPAY_UNAVAILABLE: 100,
  STREAMPAY_REJECTED: 101,
  VALIDATION: 2000,
  SESSION_EXPIRED: 2001,
  FORBIDDEN: 2002,
  NOT_FOUND: 2003,
  CONFLICT: 2004,
  RATE_LIMITED: 2005,
  CAPTCHA_FAILED: 2006,
  INVALID_CREDENTIALS: 2010,
  ACCOUNT_LOCKED: 2011,
  ACCOUNT_DISABLED: 2012,
  PASSWORD_TOKEN_INVALID: 2013,
  PASSWORD_POLICY: 2014,
  CURRENT_PASSWORD_WRONG: 2015,
  UNAUTHENTICATED: 2016,
  EMAIL_CODE_INVALID: 2017,
  EMAIL_CHANGE_EXPIRED: 2018,
  EMAIL_RESEND_TOO_SOON: 2019,
  EMAIL_UNCHANGED: 2020,
  CUSTOMER_PHONE_EXISTS: 2100,
  COORDINATOR_EMAIL_IN_USE: 2202,
  SIGN_LINK_INVALID: 2210,
  CONTRACT_ALREADY_SIGNED: 2211,
  CONTRACT_CANCELLED: 2212,
  SIGNATURE_INVALID: 2213,
  CONTRACT_SETUP_INCOMPLETE: 2214,
  UNKNOWN_PLACEHOLDER: 2215,
  CONTRACT_NOT_OPEN: 2216,
  USER_EMAIL_IN_USE: 2350,
  BOOKING_AMOUNT_REQUIRED: 2401,
  FILE_TOO_LARGE: 2700,
  FILE_TYPE_NOT_ALLOWED: 2701,
  LOOKUP_READ_ONLY: 2900,
  LOOKUP_CODE_EXISTS: 2901,
  LOOKUP_INACTIVE: 2902,
  NETWORK: -1,
} as const

export class ProblemError extends Error {
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail || problem.title)
    this.name = "ProblemError"
    this.problem = problem
  }

  get errorCode(): number {
    return this.problem.error_code
  }

  get status(): number {
    return this.problem.status
  }

  /** User-facing Arabic message (server text, rendered as plain text only). */
  get userMessage(): string {
    return this.problem.detail || this.problem.title
  }

  get fieldErrors(): FieldError[] {
    return this.problem.errors ?? []
  }

  static async from(res: Response): Promise<ProblemError> {
    const fallback: Problem = {
      type: "about:blank",
      title: res.statusText || i18n.t("errors.fallbackTitle"),
      status: res.status,
      detail: res.status >= 500 ? i18n.t("errors.server") : undefined,
      error_code: res.status >= 500 ? ErrorCode.INTERNAL : 0,
    }
    if (!res.headers.get("content-type")?.includes("json")) return new ProblemError(fallback)
    try {
      const body = (await res.json()) as Partial<Problem>
      // Trust the transport status over the body.
      return new ProblemError({ ...fallback, ...body, status: res.status })
    } catch {
      return new ProblemError(fallback)
    }
  }

  static network(cause: unknown): ProblemError {
    const err = new ProblemError({
      type: "about:blank",
      title: i18n.t("errors.networkTitle"),
      status: 0,
      detail: i18n.t("errors.networkDetail"),
      error_code: ErrorCode.NETWORK,
    })
    err.cause = cause
    return err
  }
}

export function isProblem(err: unknown): err is ProblemError {
  return err instanceof ProblemError
}

/** Best user-facing message for any thrown value (UI language). */
export function messageOf(err: unknown): string {
  if (isProblem(err)) return err.userMessage
  return i18n.t("errors.generic")
}
